import { PageContainer } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { Alert, Empty, Flex } from 'antd';
import { useEffect, useMemo } from 'react';
import MetricDateRangeFilter from '@/common/components/MetricDateRangeFilter';
import MetricProjectionWarningPopover from '@/common/components/MetricProjectionWarningPopover';
import OperationAnalysisFilterLayout, {
  operationAnalysisPageContainerStylish,
} from '@/common/components/OperationAnalysisFilterLayout';
import OperationEntityScopeSelect from '@/common/components/OperationEntityScopeSelect';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import { queryMetricEffectivePeriod } from '@/common/services/metric-effective-period';
import { queryOperationAnalysisSubjects } from '@/common/services/operation-analysis-scope';
import {
  OPERATION_ANALYSIS_METADATA_STALE_TIME,
  getMetricDateRangePresets,
  getMetricEffectivePeriodQueryKey,
  getMetricEffectiveStartDate,
} from '@/common/utils/metric-effective-period';
import {
  buildOperationEntityTree,
  getOperationAnalysisScopeQueryPatch,
  hasOperationAnalysisStores,
  resolveOperationAnalysisScopeQuery,
} from '@/common/utils/operation-analysis-scope';
import { isDebugQueryEnabled } from '@/common/utils/query';
import { buildStore360Path } from '@/common/utils/store360-navigation';
import OperationAnalysisStoreRank from '../components/OperationAnalysisStoreRank';
import OperationAnalysisTrend from '../components/OperationAnalysisTrend';
import { queryOperationFactDashboard } from '../services/fact';
import {
  DEFAULT_OPERATION_ANALYSIS_SORT_FIELD,
  DEFAULT_OPERATION_ANALYSIS_SORT_ORDER,
  OPERATION_ANALYSIS_BULK_PAGE_SIZE,
  OPERATION_ANALYSIS_PAGE_SIZE,
  getAnalysisDashboardErrorMessage,
  getAnalysisDashboardQueryKey,
  getAnalysisDateRange,
  getAnalysisGranularity,
  getLocalAnalysisStoreRank,
  getAnalysisQueryPatch,
  getAnalysisRankPage,
  getAnalysisRankPageCorrection,
  getAnalysisSort,
  getAnalysisSubjectsQueryKey,
} from '../utils/operation-analysis';

const DATE_FORMAT = 'YYYY-MM-DD';
const LEGACY_FILTER_KEYS = [
  'departmentLevel2',
  'expenseDepartment',
  'metricKey',
  'storeId',
  'subjectId',
];
const FactDashboard = () => {
  const { session } = useAppContext();
  const { organizationId, resolveOrganizationPath } = useOrganizationPath();
  const [query, setQuery] = useQueryParams();
  const debug = isDebugQueryEnabled(query);
  const identityKey =
    session.status === 'authenticated' && session.user?.id && session.role
      ? `${session.user.id}:${session.role}`
      : null;
  const {
    data: subjectResponse = {},
    isError: subjectsError,
    isPending: subjectsPending,
    isSuccess: subjectsLoaded,
  } = useQuery({
    enabled: Boolean(organizationId && identityKey),
    queryFn: () => queryOperationAnalysisSubjects({ organizationId }),
    queryKey: getAnalysisSubjectsQueryKey(organizationId, identityKey),
    staleTime: OPERATION_ANALYSIS_METADATA_STALE_TIME,
  });
  const {
    data: periodResponse = {},
    isError: periodError,
    isPending: periodPending,
    isSuccess: periodLoaded,
  } = useQuery({
    enabled: Boolean(organizationId && identityKey),
    queryFn: () => queryMetricEffectivePeriod({ organizationId }),
    queryKey: getMetricEffectivePeriodQueryKey(organizationId),
    staleTime: OPERATION_ANALYSIS_METADATA_STALE_TIME,
  });
  const effectiveStartAt = periodResponse.data?.effectiveStartAt ?? null;
  const effectiveStartDate = getMetricEffectiveStartDate(effectiveStartAt);
  const subjects = useMemo(() => subjectResponse.data || [], [subjectResponse.data]);
  const subjectTree = useMemo(() => buildOperationEntityTree(subjects), [subjects]);
  const { queryPatch: scopeQueryPatch, scope } = useMemo(
    () => resolveOperationAnalysisScopeQuery(query, subjectTree),
    [query, subjectTree],
  );
  const hasStores = hasOperationAnalysisStores(scope, subjectTree);
  const rankPage = getAnalysisRankPage(query.rankPage);
  const granularity = getAnalysisGranularity(query.granularity);
  const dateRange = getAnalysisDateRange(
    query.startDate,
    query.endDate,
    undefined,
    effectiveStartAt,
  );
  const { sortField, sortOrder } = getAnalysisSort(query.sortField, query.sortOrder);
  const queryPatch = useMemo(
    () => getAnalysisQueryPatch(query, undefined, effectiveStartAt),
    [effectiveStartAt, query],
  );
  const hasLegacyFilters = LEGACY_FILTER_KEYS.some((key) => query[key] !== undefined);

  useEffect(() => {
    if (subjectsLoaded && (Object.keys(scopeQueryPatch).length || hasLegacyFilters)) {
      setQuery(scopeQueryPatch, {
        remove: LEGACY_FILTER_KEYS,
        replace: true,
      });
    }
  }, [hasLegacyFilters, scopeQueryPatch, setQuery, subjectsLoaded]);

  useEffect(() => {
    if (
      !periodPending &&
      !periodError &&
      subjectsLoaded &&
      !Object.keys(scopeQueryPatch).length &&
      !hasLegacyFilters &&
      Object.keys(queryPatch).length
    ) {
      setQuery(queryPatch, { replace: true });
    }
  }, [
    hasLegacyFilters,
    periodError,
    periodPending,
    queryPatch,
    scopeQueryPatch,
    setQuery,
    subjectsLoaded,
  ]);

  const bulkDashboardParams = useMemo(
    () => ({
      current: 1,
      endDate: dateRange[1].format(DATE_FORMAT),
      organizationId,
      pageSize: OPERATION_ANALYSIS_BULK_PAGE_SIZE,
      scope,
      sortField: DEFAULT_OPERATION_ANALYSIS_SORT_FIELD,
      sortOrder: DEFAULT_OPERATION_ANALYSIS_SORT_ORDER,
      startDate: dateRange[0].format(DATE_FORMAT),
    }),
    [dateRange, organizationId, scope],
  );
  const dashboardQueryEnabled = Boolean(
    organizationId && identityKey && periodLoaded && subjectsLoaded && hasStores,
  );
  const {
    data: dashboardResponse = {},
    isError: dashboardError,
    isPending: dashboardPending,
    isSuccess: dashboardLoaded,
  } = useQuery({
    enabled: dashboardQueryEnabled,
    queryFn: () => queryOperationFactDashboard(bulkDashboardParams),
    queryKey: getAnalysisDashboardQueryKey(organizationId, identityKey, bulkDashboardParams),
  });
  const localStoreRank = useMemo(
    () =>
      getLocalAnalysisStoreRank({
        current: rankPage,
        data: dashboardResponse.storeRank?.data || [],
        sortField,
        sortOrder,
        total: dashboardResponse.storeRank?.meta?.total,
      }),
    [dashboardResponse.storeRank, rankPage, sortField, sortOrder],
  );
  const needsServerRank = dashboardLoaded && localStoreRank === null;
  const serverRankParams = useMemo(
    () => ({
      ...bulkDashboardParams,
      current: rankPage,
      pageSize: OPERATION_ANALYSIS_PAGE_SIZE,
      sortField,
      sortOrder,
    }),
    [bulkDashboardParams, rankPage, sortField, sortOrder],
  );
  const {
    data: serverRankResponse = {},
    isError: serverRankError,
    isPending: serverRankPending,
  } = useQuery({
    enabled: dashboardQueryEnabled && needsServerRank,
    queryFn: () => queryOperationFactDashboard(serverRankParams),
    queryKey: getAnalysisDashboardQueryKey(organizationId, identityKey, serverRankParams),
  });
  const storeRank = needsServerRank ? serverRankResponse.storeRank : localStoreRank;
  const correctedRankPage = getAnalysisRankPageCorrection({
    isSuccess: dashboardLoaded,
    rankPage,
    total: dashboardResponse.storeRank?.meta?.total,
  });

  useEffect(() => {
    if (correctedRankPage) {
      setQuery({ rankPage: correctedRankPage }, { replace: true });
    }
  }, [correctedRankPage, setQuery]);

  let dashboardContent = !subjectsPending && !periodPending && (
    <Empty description="当前范围暂无可分析门店，请重新选择经营范围" />
  );
  if (hasStores) {
    dashboardContent = (
      <Flex vertical gap="large">
        <OperationAnalysisTrend
          granularity={granularity}
          loading={dashboardPending}
          series={dashboardResponse.series?.[granularity] || []}
          onGranularityChange={(nextGranularity) => setQuery({ granularity: nextGranularity })}
        />
        <OperationAnalysisStoreRank
          current={storeRank?.meta?.page || rankPage}
          dataSource={storeRank?.data || []}
          getStoreLink={({ operationEntityId }) =>
            resolveOrganizationPath(
              buildStore360Path(operationEntityId, {
                endDate: dateRange[1].format(DATE_FORMAT),
                startDate: dateRange[0].format(DATE_FORMAT),
              }),
            )
          }
          loading={dashboardPending || (needsServerRank && serverRankPending)}
          sortField={sortField}
          sortOrder={sortOrder}
          total={storeRank?.meta?.total || dashboardResponse.storeRank?.meta?.total || 0}
          onPageChange={(nextPage) => setQuery({ rankPage: nextPage })}
          onSortChange={(nextSortField, nextSortOrder) =>
            setQuery({
              rankPage: 1,
              sortField: nextSortField,
              sortOrder: nextSortOrder,
            })
          }
        />
      </Flex>
    );
  }
  const dashboardErrorMessage = getAnalysisDashboardErrorMessage({
    dashboardError: dashboardError || (needsServerRank && serverRankError),
    periodError,
    queryEnabled: dashboardQueryEnabled,
    subjectsError,
  });
  if (dashboardErrorMessage) {
    dashboardContent = <Alert showIcon title={dashboardErrorMessage} type="error" />;
  }

  return (
    <PageContainer
      extra={
        <OperationAnalysisFilterLayout
          actions={
            debug ? <MetricProjectionWarningPopover warnings={dashboardResponse.warnings} /> : null
          }
          dateFilter={
            <MetricDateRangeFilter
              ariaLabel="看板周期"
              minDate={effectiveStartDate || undefined}
              presets={getMetricDateRangePresets(effectiveStartDate)}
              value={dateRange}
              onChange={(range) =>
                setQuery({
                  endDate: range?.[1]?.format(DATE_FORMAT),
                  startDate: range?.[0]?.format(DATE_FORMAT),
                })
              }
            />
          }
          scopeFilter={
            <OperationEntityScopeSelect
              treeData={subjectTree}
              value={scope}
              onChange={(nextScope) =>
                setQuery({
                  ...getOperationAnalysisScopeQueryPatch(nextScope),
                  rankPage: 1,
                })
              }
            />
          }
        />
      }
      loading={subjectsPending || periodPending}
      stylish={operationAnalysisPageContainerStylish}
    >
      {dashboardContent}
    </PageContainer>
  );
};

export default FactDashboard;
